/**
 * Lenavio Learn — course library + assignment manager.
 *
 * HR_ADMIN+ surface. Lists every course in the org, lets an admin
 * create/edit a course inline (slide-over modal), and pops a
 * separate "Assign" modal that targets employees + departments.
 *
 * Employees use ``/training/my-courses`` instead. The library is
 * intentionally HR-only; pricing and product positioning treat
 * the catalog itself as a back-office tool.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BarChart3,
  Edit3,
  GraduationCap,
  Plus,
  Trash2,
  UserPlus,
} from "lucide-react";

import {
  assignCourse,
  createCourse,
  deleteCourse,
  listCourses,
  updateCourse,
  type AssignmentCreatePayload,
  type CourseCategory,
  type CourseCreatePayload,
  type Recurrence,
  type TrainingCourse,
} from "@/api/training";
import { listEmployees } from "@/api/employees";
import { listDepartments } from "@/api/organizations";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { ExportCsvButton } from "@/components/shared/ExportCsvButton";
import { useToast } from "@/hooks/useToast";
import { pageTransition } from "@/lib/utils";

const CATEGORY_OPTIONS: { value: CourseCategory; label: string }[] = [
  { value: "compliance", label: "Compliance" },
  { value: "skills", label: "Skills" },
  { value: "onboarding", label: "Onboarding" },
  { value: "custom", label: "Custom" },
];

const RECURRENCE_OPTIONS: { value: Recurrence; label: string }[] = [
  { value: "one_time", label: "One-time" },
  { value: "annual", label: "Annual" },
  { value: "quarterly", label: "Quarterly" },
];

interface DepartmentRow {
  id: string;
  name: string;
}
interface EmployeeListRow {
  id: string;
  first_name: string;
  last_name: string;
  department_id: string | null;
}

export function TrainingCoursesPage() {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [courses, setCourses] = useState<TrainingCourse[] | null>(null);
  const [editing, setEditing] = useState<TrainingCourse | "new" | null>(null);
  const [assigning, setAssigning] = useState<TrainingCourse | null>(null);

  const refresh = async () => {
    try {
      setCourses(await listCourses());
    } catch {
      addToast("error", "Could not load courses.");
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (c: TrainingCourse) => {
    if (!confirm(`Archive "${c.title}"? This hides it from the library; existing assignments stay.`)) {
      return;
    }
    try {
      await deleteCourse(c.id);
      addToast("success", "Course archived.");
      void refresh();
    } catch {
      addToast("error", "Could not archive course.");
    }
  };

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Lenavio Learn"
        subtitle="Course library, assignments, compliance reporting."
        actions={
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => navigate("/training/dashboard")}
            >
              <BarChart3 className="h-4 w-4" />
              Dashboard
            </Button>
            <ExportCsvButton resource="training" />
            <Button onClick={() => setEditing("new")}>
              <Plus className="h-4 w-4" />
              New course
            </Button>
          </div>
        }
      />

      {courses === null ? (
        <div className="grid grid-cols-1 gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 rounded bg-border/30 animate-pulse" />
          ))}
        </div>
      ) : courses.length === 0 ? (
        <EmptyState onCreate={() => setEditing("new")} />
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {courses.map((c) => (
            <CourseRow
              key={c.id}
              course={c}
              onEdit={() => setEditing(c)}
              onAssign={() => setAssigning(c)}
              onDelete={() => handleDelete(c)}
            />
          ))}
        </div>
      )}

      {editing && (
        <CourseFormModal
          course={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void refresh();
          }}
        />
      )}

      {assigning && (
        <AssignModal
          course={assigning}
          onClose={() => setAssigning(null)}
          onAssigned={() => {
            setAssigning(null);
            void refresh();
          }}
        />
      )}
    </motion.div>
  );
}


// ── Row ────────────────────────────────────────────────────────────


function CourseRow({
  course,
  onEdit,
  onAssign,
  onDelete,
}: {
  course: TrainingCourse;
  onEdit: () => void;
  onAssign: () => void;
  onDelete: () => void;
}) {
  return (
    <Card className="flex items-center justify-between gap-4">
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <div className="rounded-lg bg-accent/10 p-2 mt-0.5">
          <GraduationCap className="h-5 w-5 text-accent" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="text-base font-semibold font-heading text-text-primary truncate">
              {course.title}
            </h3>
            {course.is_default_template && (
              <Badge variant="default" className="uppercase tracking-wide text-[10px]">
                Lenavio default
              </Badge>
            )}
            <Badge variant="info">{course.category}</Badge>
            <Badge variant="default">{course.recurrence.replace("_", " ")}</Badge>
            {course.is_required && <Badge variant="warning">Required</Badge>}
            {!course.is_active && <Badge variant="default">Archived</Badge>}
          </div>
          {course.description && (
            <p className="text-sm text-text-muted line-clamp-2">
              {course.description}
            </p>
          )}
          <div className="flex items-center gap-3 mt-2 text-xs text-text-muted">
            {course.duration_minutes && <span>{course.duration_minutes} min</span>}
            <span>·</span>
            <span>{course.assignment_count} assigned</span>
            <span>·</span>
            <span>{course.completion_count} completed</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 shrink-0">
        <Button size="sm" onClick={onAssign}>
          <UserPlus className="h-3.5 w-3.5" />
          Assign
        </Button>
        <Button variant="secondary" size="sm" onClick={onEdit}>
          <Edit3 className="h-3.5 w-3.5" />
          Edit
        </Button>
        <Button variant="ghost" size="sm" onClick={onDelete} aria-label="Archive">
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </Card>
  );
}


// ── Course form modal ─────────────────────────────────────────────


function CourseFormModal({
  course,
  onClose,
  onSaved,
}: {
  course: TrainingCourse | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = course !== null;
  const { addToast } = useToast();

  const [title, setTitle] = useState(course?.title ?? "");
  const [description, setDescription] = useState(course?.description ?? "");
  const [category, setCategory] = useState<CourseCategory>(
    (course?.category as CourseCategory) ?? "custom",
  );
  const [contentUrl, setContentUrl] = useState(course?.content_url ?? "");
  const [duration, setDuration] = useState(
    course?.duration_minutes?.toString() ?? "",
  );
  const [isRequired, setIsRequired] = useState(course?.is_required ?? false);
  const [recurrence, setRecurrence] = useState<Recurrence>(
    (course?.recurrence as Recurrence) ?? "one_time",
  );
  const [submitting, setSubmitting] = useState(false);

  const handleSave = async () => {
    if (!title.trim()) {
      addToast("error", "Title is required.");
      return;
    }
    const payload: CourseCreatePayload = {
      title: title.trim(),
      description: description.trim() || undefined,
      category,
      content_url: contentUrl.trim() || undefined,
      duration_minutes: duration ? Number(duration) : undefined,
      is_required: isRequired,
      recurrence,
    };
    setSubmitting(true);
    try {
      if (isEdit && course) {
        await updateCourse(course.id, payload);
      } else {
        await createCourse(payload);
      }
      addToast("success", isEdit ? "Course updated." : "Course created.");
      onSaved();
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        "Could not save course.";
      addToast("error", detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? "Edit course" : "New course"}
      size="lg"
    >
      <div className="space-y-3">
        <Input
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Select
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value as CourseCategory)}
            options={CATEGORY_OPTIONS}
          />
          <Select
            label="Recurrence"
            value={recurrence}
            onChange={(e) => setRecurrence(e.target.value as Recurrence)}
            options={RECURRENCE_OPTIONS}
          />
        </div>
        <Input
          label="Content URL"
          value={contentUrl}
          onChange={(e) => setContentUrl(e.target.value)}
          placeholder="https://your-lms.com/course/xyz or video link"
          hint="Where the actual training content lives. Lenavio tracks completion; the content can sit anywhere."
        />
        <Input
          type="number"
          label="Duration (minutes)"
          value={duration}
          onChange={(e) => setDuration(e.target.value)}
          min={0}
          max={10000}
        />
        <label className="flex items-center gap-3 mt-2">
          <input
            type="checkbox"
            checked={isRequired}
            onChange={(e) => setIsRequired(e.target.checked)}
            className="h-4 w-4 rounded border-border text-accent focus:ring-accent"
          />
          <span className="text-sm text-text-primary">
            Required — surfaces on the compliance report
          </span>
        </label>

        <div className="flex justify-end gap-3 pt-3">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSave} isLoading={submitting}>
            {isEdit ? "Save changes" : "Create course"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}


// ── Assign modal ──────────────────────────────────────────────────


function AssignModal({
  course,
  onClose,
  onAssigned,
}: {
  course: TrainingCourse;
  onClose: () => void;
  onAssigned: () => void;
}) {
  const { addToast } = useToast();
  const [departments, setDepartments] = useState<DepartmentRow[]>([]);
  const [employees, setEmployees] = useState<EmployeeListRow[]>([]);
  const [allEmployees, setAllEmployees] = useState(false);
  const [selDepts, setSelDepts] = useState<Set<string>>(new Set());
  const [selEmps, setSelEmps] = useState<Set<string>>(new Set());
  const [dueDate, setDueDate] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    void Promise.all([
      listDepartments().then((d) =>
        setDepartments(d.map((r) => ({ id: r.id, name: r.name }))),
      ),
      listEmployees(1, 200).then((res) =>
        setEmployees(
          res.items.map((e) => ({
            id: e.id,
            first_name: e.first_name,
            last_name: e.last_name,
            department_id: (e as unknown as { department_id?: string | null })
              .department_id ?? null,
          })),
        ),
      ),
    ]);
  }, []);

  const toggle = (set: Set<string>, id: string) => {
    const next = new Set(set);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  };

  const targetCount = useMemo(() => {
    if (allEmployees) return employees.length;
    const ids = new Set(selEmps);
    for (const e of employees) {
      if (e.department_id && selDepts.has(e.department_id)) ids.add(e.id);
    }
    return ids.size;
  }, [allEmployees, employees, selDepts, selEmps]);

  const handleAssign = async () => {
    if (!allEmployees && selEmps.size === 0 && selDepts.size === 0) {
      addToast("error", "Pick at least one employee or department.");
      return;
    }
    setSubmitting(true);
    try {
      const payload: AssignmentCreatePayload = {
        course_id: course.id,
        all_employees: allEmployees,
        employee_ids: allEmployees ? [] : Array.from(selEmps),
        department_ids: allEmployees ? [] : Array.from(selDepts),
        due_date: dueDate || undefined,
      };
      const res = await assignCourse(payload);
      addToast(
        "success",
        `Assigned to ${res.created} employee${res.created === 1 ? "" : "s"} (skipped ${res.skipped_existing} already on it).`,
      );
      onAssigned();
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        "Could not assign course.";
      addToast("error", detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={`Assign ${course.title}`} size="lg">
      <div className="space-y-4">
        <Input
          type="date"
          label="Due date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          hint="Optional — leave blank for no deadline."
        />

        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={allEmployees}
            onChange={(e) => setAllEmployees(e.target.checked)}
            className="h-4 w-4 rounded border-border text-accent focus:ring-accent"
          />
          <span className="text-sm text-text-primary font-medium">
            Assign to <strong>all active employees</strong>
          </span>
        </label>

        {!allEmployees && (
          <>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-2">
                Departments
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {departments.map((d) => (
                  <label
                    key={d.id}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer ${
                      selDepts.has(d.id) ? "border-accent bg-accent/5" : "border-border"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selDepts.has(d.id)}
                      onChange={() => setSelDepts((s) => toggle(s, d.id))}
                      className="h-4 w-4 rounded border-border text-accent focus:ring-accent"
                    />
                    <span className="text-sm">{d.name}</span>
                  </label>
                ))}
                {departments.length === 0 && (
                  <span className="text-xs text-text-muted">No departments yet.</span>
                )}
              </div>
            </div>

            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-2">
                Specific employees
              </h4>
              <div className="max-h-48 overflow-y-auto rounded-lg border border-border bg-background">
                {employees.map((e) => (
                  <label
                    key={e.id}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-surface cursor-pointer border-b border-border/50 last:border-b-0"
                  >
                    <input
                      type="checkbox"
                      checked={selEmps.has(e.id)}
                      onChange={() => setSelEmps((s) => toggle(s, e.id))}
                      className="h-4 w-4 rounded border-border text-accent focus:ring-accent"
                    />
                    <span className="text-sm">
                      {e.first_name} {e.last_name}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="rounded-lg bg-accent/5 border border-accent/20 px-4 py-3 text-sm">
          <strong>{targetCount}</strong> employee{targetCount === 1 ? "" : "s"} will receive this course.
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleAssign} isLoading={submitting}>
            Assign
          </Button>
        </div>
      </div>
    </Modal>
  );
}


function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <Card className="text-center py-10">
      <div className="rounded-xl bg-accent/10 mx-auto h-12 w-12 flex items-center justify-center mb-3">
        <GraduationCap className="h-6 w-6 text-accent" />
      </div>
      <h3 className="text-base font-semibold text-text-primary">
        No courses yet
      </h3>
      <p className="text-sm text-text-muted mt-1 mb-4 max-w-md mx-auto">
        New workspaces get five starter courses (sexual harassment,
        workplace safety, data security, code of conduct, D&I) seeded
        automatically. Build your own here.
      </p>
      <Button onClick={onCreate}>
        <Plus className="h-4 w-4" />
        New course
      </Button>
    </Card>
  );
}
